<template>
  <div
    data-state="active"
    data-orientation="horizontal"
    role="tabpanel"
    aria-labelledby="radix-_r_4m_-trigger-history"
    id="radix-_r_4m_-content-history"
    tabindex="0"
    class="flex flex-col mt-2 ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 h-full overflow-auto"
  >
    <div
      class="flex-1 flex flex-col rounded-lg border bg-card text-card-foreground shadow-2xs"
      data-v0-t="card"
    >
      <div class="flex-1 flex flex-col p-6 relative overflow-hidden">
        <el-table
          class="flex-1"
          v-loading="tableCtrl.state.loading"
          element-loading-background="rgba(0, 0, 0, 0.8)"
          :data="tableCtrl.state.tableData"
          border
          height="auto"
          style="width: 100%"
        >
          <el-table-column prop="ruleName" label="Rule" min-width="160">
            <template #default="{ row }">
              {{ row.ruleName || "--" }}
            </template>
          </el-table-column>
          <el-table-column prop="assetName" label="Asset" min-width="140">
            <template #default="{ row }">
              {{ row.assetName || "--" }}
            </template>
          </el-table-column>
          <el-table-column prop="deviceKey" label="Device Key" min-width="200">
            <template #default="{ row }">
              {{ formatDeviceKey(row.deviceKey) }}
            </template>
          </el-table-column>
          <el-table-column prop="status" label="Status" width="140">
            <template #default="{ row }">
              <div
                class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2"
                :class="statusClasses(row.status)"
              >
                {{ row.status || "--" }}
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="escalated" label="Escalated" width="130">
            <template #default="{ row }">
              <span
                :class="[
                  'font-medium',
                  row.escalated ? 'text-red-600' : 'text-slate-500',
                ]"
              >
                {{ row.escalated ? "Yes" : "No" }}
              </span>
            </template>
          </el-table-column>
          <el-table-column
            prop="feedbackRecipientNames"
            label="Feedback Recipients"
            min-width="220"
          >
            <template #default="{ row }">
              {{ formatRecipients(row.feedbackRecipientNames) }}
            </template>
          </el-table-column>
          <el-table-column
            prop="lastFeedbackAt"
            label="Last Feedback"
            width="190"
          >
            <template #default="{ row }">
              {{ formatTimestamp(row.lastFeedbackAt) }}
            </template>
          </el-table-column>
          <el-table-column label="Actions" width="100" fixed="right">
            <template #default="{ row }">
              <el-button @click="handleViewDetail(row)" icon="el-icon-view"/>
            </template>
          </el-table-column>
        </el-table>
        <div class="flex mt-4 justify-end">
          <el-pagination
            @size-change="(val) => tableCtrl.setPageSize(val)"
            @current-change="(val) => tableCtrl.setCurrentPage(val)"
            :current-page="tableCtrl.state.pagination.page"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="tableCtrl.state.pagination.pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="tableCtrl.state.pagination.total"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { TableController } from "@/controllers/TableController";
import { getNotificationHistorySummary } from "@/api";

const normalizeSearchValue = (value) => {
  const str = typeof value === "string" ? value.trim() : "";
  return str || undefined;
};

export default {
  name: "AlertHistory",
  props: {
    searchText: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      tableCtrl: new TableController({
        loadMethod: getNotificationHistorySummary,
        autoLoad: true,
        isClientPaged: false, // Server-side pagination
        pageSize: 10,
        keyField: "eventId",
        queryParameters: {
          deviceKey: normalizeSearchValue(this.searchText),
        },
      }),
    };
  },
  watch: {
    searchText(value) {
      this.applyServerSearch(value);
    },
  },
  methods: {
    applyServerSearch(value) {
      if (!this.tableCtrl) {
        return;
      }
      const normalized = normalizeSearchValue(value);
      if (this.tableCtrl.queryParameters.search === normalized) {
        return;
      }
      this.tableCtrl.setQueryParameters({ deviceKey: normalized });
    },
    formatTimestamp(value) {
      if (!value) {
        return "--";
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return "--";
      }
      return date.toLocaleString();
    },
    formatDeviceKey(value) {
      return value || "--";
    },
    formatRecipients(names) {
      if (!names) {
        return "--";
      }
      return names;
    },
    statusClasses(status) {
      return {
        "bg-green-100 text-green-800": status === "SENT",
        "bg-red-100 text-red-800": status === "FAILED",
        "bg-orange-100 text-orange-800": status === "PENDING",
        "bg-slate-100 text-slate-800":
          status !== "SENT" && status !== "FAILED" && status !== "PENDING",
      };
    },
    async mockDataList() {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({
            totalCount: 100,
            pageSize: 10,
            totalPage: 10,
            currPage: 1,
            list: Array.from({ length: 20 }, (_, i) => ({
              id: i + 1,
              userId: 1000 + i,
              eventId: 888 + i,
              ruleId: 123 + i,
              recipientId: 10 + i,
              deviceKeys: `dev-${i + 1}`,
              message: `Alert message ${i + 1}`,
              status: ["SENT", "FAILED", "PENDING"][i % 3],
              errorMsg: null,
              providerMsgId: `MSGID${i + 1}`,
              sentAt: 1764901172000 + i * 100000,
              createdAt: 1764901172000 + i * 100000,
              acknowledgedBy: i % 2 === 0 ? `User${i + 1}` : "",
            })),
          });
        }, 2000);
      });
    },
    async handleViewDetail(row) {
      await this.$loaderWindow({
        title: `View Alert Detail`,
        component: () => import("./DetailHistoryAlert.vue"),
        width: "70%",
        showHeader: false,
        padding: false,
        closeOnClickModal: false,
        closeOnPressEscape: false,
        props: { item: row },
      });
    },
  },
};
</script>
